// PayAndCreate — event publish control.
//
// T2 (App Store 3.1.1): TournyFlex v1 launches FREE with no in-app purchase,
// so publishing is always free — there is no fee and no steering to any
// web-checkout page. Every event (including big outings > 8 players and
// multi-round events that used to be priced) publishes directly via
// POST /publish-event. The web-payment backend stays in place for the
// StoreKit IAP follow-up, but no in-app UI offers or steers to web payment.
//
// The component keeps its roster-completeness gate: an event still can't be
// published until every scheduled slot has a real name (F-052 / D-268). The
// server (publish-event.js) remains the authority on both.
//
// Props:
//   event_id           — required.
//   players_scheduled  — required. Positive integer.
//   rounds_scheduled   — required. Positive integer.
//   name               — optional. Shown for context ("Publish 'Charity Scramble'").
//   named_count        — optional. Number of roster players with a REAL name
//                        (not blank, not an anonymous "Player N" placeholder).
//                        When omitted the client gate is inert and the server
//                        still enforces the rule (surfacing `roster_incomplete`).
//   onPublished        — optional. Called with { published_at } on success.

import { useState } from 'react'
import { supabase } from '../lib/supabase'

export default function PayAndCreate({
  event_id,
  players_scheduled,
  rounds_scheduled,
  name,
  named_count,
  onPublished,
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const validCounts = Number(players_scheduled) > 0 && Number(rounds_scheduled) > 0

  // F-052 / D-268 — roster-completeness gate. When named_count is supplied,
  // block publish until every scheduled slot has a real name. When it's not
  // supplied (backward-safe default for callers that don't pass it), the
  // client gate is inert and the server remains the enforcer.
  const gateActive = named_count != null && Number(players_scheduled) > 0
  const namedCount = Math.max(0, Number(named_count) || 0)
  const namesRemaining = gateActive
    ? Math.max(0, Number(players_scheduled) - namedCount)
    : 0
  const rosterComplete = !gateActive || namesRemaining === 0

  const publish = async () => {
    if (!event_id || !validCounts || busy || !rosterComplete) return
    setError(null)
    setBusy(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        setError('Please sign in again to continue.')
        setBusy(false)
        return
      }
      const res = await fetch('/.netlify/functions/publish-event', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ event_id }),
      })
      const json = await res.json()
      if (!res.ok) {
        throw new Error(json.error || 'Publish failed — please try again.')
      }
      onPublished?.({ published_at: json.published_at })
    } catch (e) {
      setError(e.message || 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  if (!validCounts) {
    return (
      <div className="card">
        <p className="text-sm text-muted" style={{ margin: 0 }}>
          Enter player count and round count before publishing.
        </p>
      </div>
    )
  }

  return (
    <div className="card">
      <h3 style={{ margin: 0, marginBottom: 6 }}>Publish event</h3>

      <p className="text-sm text-muted" style={{ marginBottom: 10 }}>
        {name ? <><strong>{name}</strong> · </> : null}
        {players_scheduled} player{Number(players_scheduled) === 1 ? '' : 's'}
        {' × '}
        {rounds_scheduled} round{Number(rounds_scheduled) === 1 ? '' : 's'}
        {' · free'}
      </p>

      {!rosterComplete && (
        <p className="text-sm" style={{ color: 'var(--gold)', marginBottom: 10 }}>
          Add a name for each of your {Number(players_scheduled)} players
          {' '}({namesRemaining} left) before publishing. Anonymous
          {' '}"Player" slots are only for casual rounds.
        </p>
      )}

      {error && (
        <p className="text-sm" style={{ color: 'var(--danger, #b23)', marginBottom: 10 }}>
          {error}
        </p>
      )}

      <button
        className="btn btn-primary btn-full"
        disabled={busy || !rosterComplete}
        onClick={publish}
      >
        {busy ? 'Publishing…' : 'Publish event'}
      </button>
    </div>
  )
}
